/**
 * Javascript file for the canvas overview page of the current business model.
 * @author Adrian Rebmann, Steffen Schuhmann, Andrea Villeda
 */

import {
  getBusinessModelByHash,
  updateBusinessmodel,
  getBusinessModelSubgraph,
  updateBrick,
  getTechnologiesForBm
} from "../../../api";
import jsPDF from "jspdf";
import $ from "jquery";
const json = require("../../../../static/pdflogo.json");
export default {
  name: "BusinessModel",
  props: ["bmname"],

  /**
   * The vue instance's data object.
   * @property {boolean} customAdd - Activate the option to add custom information.
   * @property {string} currentAspect - The current aspect.
   * @property {string} aspecttoadd.description -The description of the aspect to add.
   * @property {string} aspecttoadd.name - The name of the aspect to add.
   * @property {boolean} aspecttoadd.brick - Whether the aspect to add is part of a brick.
   * @property {boolean} listView - Activate the list view.
   * @property {boolean} loading - Information is being loaded.
   * @property {boolean} saveActive - Activate the "save" pop-up.
   * @property {boolean} setDoneActive - Activate the "are you done?" pop-up.
   * @property {boolean} setNotDoneActive - Activate the "mark as in progress?" pop-up.
   * @property {boolean} saveActiveBeforeLeave - Activate the "save before leave" pop-up.
   * @property {object} businessmodel - The current business model.
   * @property {boolean} setRecommendationsActive - Activate the "technology recommendations" pop-up.
   * @property {object} recommendations - The recommended technologies for the current business model.
   */
  data: () => ({
    customAdd: false,
    currentaspect: "",
    aspecttoadd: {
      description: "",
      name: "",
      brick: false
    },
    listView: false,
    loading: false,
    saveActive: false,
    setDoneActive: false,
    setNotDoneActive: false,
    saveActiveBeforeLeave: false,
    businessmodel: null,
    setRecommendationsActive: false,
    recommendations: []
  }),

  /**
   * Called after the instance has been created.
   */
  created() {
    this.fetchData();
  },

  /**
   * Expressions to watch.
   */
  watch: {
    // call again the method if the route changes
    $route: "fetchData"
  },

  /**
   * 
   */
  methods: {
    /**
     * Loads the "BMLoad" page when the return button is clicked.
     */
    goBack() {
      this.$router.push({
        name: "BMLoad"
      });
    },

    /**
     * Shortens the description.
     * @param {*} desc - The description of the business model.
     */
    shorten(desc) {
      var res = desc.substring(0, 500) + "...";
      return res;
    },

    /**
     * Adds a new element instance.
     */
    addToElementInstances() {
      //If the aspect to add is part of a brick add the aspect and
      //redirect to the page of the brick.
      if (this.aspecttoadd.brick) {
        getBusinessModelSubgraph(
          this.businessmodel.hash,
          this.currentaspect
        ).then(sub => {
          sub.elementinstances.unshift({
            children: [],
            description: this.aspecttoadd.description,
            element: null,
            hash: "",
            name: this.aspecttoadd.name
          });
          updateBrick(this.businessmodel.hash, this.currentaspect, sub).then(
            () => {
              this.aspecttoadd.name = "";
              this.aspecttoadd.description = "";
              this.customAdd = false;
              this.$router.push({
                name: "Brick",
                params: {
                  hash: this.businessmodel.hash,
                  name: this.currentaspect
                }
              });
            }
          );
        });
      } else {
        this.aspecttoadd.name = "";
        this.aspecttoadd.description = "";
      }
    },

    /**
     * Redirects to the "BMMenu" page.
     */
    goToMenu() {
      this.$router.push({
        name: "BMMenu"
      });
    },

    /**
     * Redirects to the target brick page.
     * @param {*} brick - The target brick element.
     */
    goToBrick(brick) {
      this.$router.push({
        name: "Brick",
        params: {
          hash: this.businessmodel.hash,
          name: brick
        }
      });
    },

    /**
     * The user confirmed that the business model is complete.
     */
    onConfirm() {
      this.businessmodel.status = "Fertig";
    },

    /**
     * The user cancelled the signalization that the business model is complete.
     */
    onCancel() { },

    /**
     * The user confirmed that the business model is to be signalised as 
     * being in processing.
     */
    onConfirmNot() {
      this.businessmodel.status = "in Bearbeitung";
    },
    /**
      * The user cancelled the signalization of the business model 
      * as being in processing.
      */
    onCancelNot() { },

    /**
     * Creates the PDF version of the current business model.
     */
    exportPDF() {
      //841x594
      var doc = new jsPDF({ orientation: "landscape", format: "a1" });
      let x = 20;
      let y = 20;
      var imageData = json.logo;

      //Kundensegment
      x = 660;
      y = 20;
      doc.setFontSize(20);
      doc.text(x, y, "Kundensegment");
      doc.setFontSize(16);
      let elms = this.getKundensegment();
      let aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;
          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }
        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Wertangebot
      x = 340;
      y = 20;
      doc.setFontSize(20);
      doc.text(x, y, "Wertangebot");
      doc.setFontSize(16);
      elms = this.getWertangebot();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;
          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }
        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Schlüsselpartner
      x = 20;
      y = 20;
      doc.setFontSize(20);
      doc.text(x, y, "Schlüsselpartnerschaften");
      doc.setFontSize(16);
      elms = this.getSchlusselpartnerschaften();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;

          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }
        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Schlüsselressourcen
      y = 210;
      x = 180;
      doc.setFontSize(20);
      doc.text(x, y, "Schlüsselressourcen");
      doc.setFontSize(16);
      elms = this.getSchlusselressourcen();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;

          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }
        if (y > 380) {
          y = 220;
          x += 80;
        }

        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Schlüsselaktivitäten
      y = 20;
      x = 180;
      doc.setFontSize(20);
      doc.text(x, y, "Schlüsselaktivitäten");
      doc.setFontSize(16);
      elms = this.getSchlusselaktivitaten();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;

          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }

        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Einnahmequellen
      y = 400;
      x = 420;
      doc.setFontSize(20);
      doc.text(x, y, "Einnahmequellen");
      doc.setFontSize(16);
      elms = this.getEinnahmequellen();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;

          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }

        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Kanäle
      x = 500;
      y = 210;
      doc.setFontSize(20);
      doc.text(x, y, "Kanäle");
      doc.setFontSize(16);
      elms = this.getKanale();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;

          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }
        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Kundenbeziehung
      x = 500;
      y = 20;
      doc.setFontSize(20);
      doc.text(x, y, "Kundenbeziehung");
      doc.setFontSize(16);
      elms = this.getKundensegment();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;

          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }
        doc.text(x, y, aggregate);
        y += 10;
      }
      y += 10;

      //Kostenstruktur
      x = 20;
      y = 400;
      doc.setFontSize(20);
      doc.text(x, y, "Kostenstruktur");
      doc.setFontSize(16);
      elms = this.getKostenstruktur();
      aggregate = "";
      y += 10;
      for (let j = 0; j < elms.length; j++) {
        aggregate = "";
        doc.setFontSize(16);
        doc.text(x, y, elms[j].name);
        y += 5;
        doc.setFontSize(14);
        for (let i = 0; i < elms[j].children.length; i++) {
          aggregate += elms[j].children[i].name;
          doc.text(x, y, aggregate);
          y += 5;
          x += 5;
          aggregate = "";
          for (let k = 0; k < elms[j].children[i].children.length; k++) {
            aggregate += elms[j].children[i].children[k].name;
            doc.text(x, y, aggregate);
            aggregate += "; ";
          }
          y += 5;
          x -= 5;
          aggregate = "";
        }

        doc.text(x, y, aggregate);
        y += 10;
      }

      doc.rect(15, 10, 160, 380, "S");
      doc.rect(175, 10, 160, 380, "S");
      doc.rect(175, 200, 160, 190, "S");
      doc.rect(335, 10, 160, 380, "S");
      doc.rect(495, 10, 160, 380, "S");
      doc.rect(495, 200, 160, 190, "S");
      doc.rect(655, 10, 160, 380, "S");
      doc.rect(15, 390, 400, 150, "S");
      doc.rect(415, 390, 400, 150, "S");
      doc.setFontSize(44);
      doc.text(70, 570, "Name: " + this.businessmodel.name);
      doc.addImage(imageData, "JPEG", 700, 550, 100, 40);

      //save as pdf
      doc.save(this.businessmodel.name + ".pdf");
    },

    /**
     * If there is a brick with questions still unanswered,
     * it redirects to said brick.
     */
    continueWithQuestions() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (!this.businessmodel.bricks[i].donewithquestions) {
          this.$router.push({
            name: "Brick",
            params: {
              hash: this.businessmodel.hash,
              name: this.businessmodel.bricks[i].name
            }
          });
        }
      }
    },

    /**
     * Determines if the user is done answering the questions.
     */
    checkContinueWithQuestions() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (!this.businessmodel.bricks[i].donewithquestions) {
          return true;
        }
      }
      return false;
    },

    /**
     * Saves the current business model.
     */
    saveModel() {
      updateBusinessmodel(this.businessmodel.hash, this.businessmodel).then(
        res => {
          this.fetchData();
        }
      );
    },

    /**
     * Saves the current business model and loads the "BMLoad" page.
     */
    saveModelAndGoBack() {
      updateBusinessmodel(this.businessmodel.hash, this.businessmodel).then(
        res => {
          this.businessmodel = res;
          this.goBack();
        }
      );
    },

    /**
     * Fetches the business model's data stored in the backend.
     */
    fetchData() {
      this.businessmodel = null;
      this.loading = true;
      getBusinessModelByHash(this.$route.params.hash)
        .then(res => {
          console.log(res);
          getTechnologiesForBm(res.hash)
            .then(recommendations => {
              this.businessmodel = res;
              this.loading = false;
              this.recommendations = recommendations;
            })
            .catch(err => {
              //console.log(err);
            });
        })
        .catch(err => {
          //console.log(err);
        });
    },

    /**
     * Returns the value proposition for the current business model.
     */
    getWertangebot() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Wertangebot") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the key activities for the current business model.
     */
    getSchlusselaktivitaten() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Schlüsselaktivitäten") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the key ressources for the current business model.
     */
    getSchlusselressourcen() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Schlüsselressourcen") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the sources of income for the current business model.
     */
    getEinnahmequellen() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Einnahmequellen") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the channels for the current business model.
     */
    getKanale() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Kanäle") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the key partners for the current business model.
     */
    getSchlusselpartnerschaften() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Schlüsselpartnerschaften") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the cost structure for the current business model.
     */
    getKostenstruktur() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Kostenstruktur") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the customer relationships for the current business model.
     */
    getKundenbeziehung() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Kundenbeziehung") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    },

    /**
     * Returns the customer segment for the current business model.
     */
    getKundensegment() {
      for (let i = 0; i < this.businessmodel.bricks.length; i++) {
        if (this.businessmodel.bricks[i].name == "Kundensegment") {
          return this.businessmodel.bricks[i].elementinstances;
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/company/businessmodel/businessmodel.js